import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { IMAGES } from "@/data/site";
import { PageHero, SectionHeader } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { Editable } from "@/components/Editable";
import ProjectShowcase from "@/components/ProjectShowcase";
import { Calculator, Zap, Cpu, Gauge, Sun, Battery, ArrowUpRight, CheckCircle2 } from "lucide-react";

const SPEC_ICONS = [Sun, Cpu, Gauge, Battery];

export default function Solar() {
  const { t } = useApp();
  const s = t.solarP;
  return (
    <div>
      <PageHero image={IMAGES.solarField} eyebrow={s.eyebrow} title={t.solar.hero} subtitle={t.solar.heroSub}>
        <Link to="/solar-calculator" className="oj-btn-gold" data-testid="solar-calculator-btn"><Calculator className="w-4 h-4" /> {t.nav.calculator}</Link>
        <Link to="/contact" className="oj-btn-ghost">{s.dpr} <ArrowUpRight className="w-4 h-4" /></Link>
      </PageHero>

      {/* Corporate Overview — Solar Energy & Renewable Energy */}
      <Section sid="solar.profile" name="Solar Overview" className="oj-section bg-[#E9F5F4]/40 border-b border-teal-100" data-testid="solar-profile">
        <div className="oj-container grid grid-cols-1 lg:grid-cols-3 gap-10">
          <Reveal className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <div className="oj-eyebrow mb-3"><Sun className="w-4 h-4" /> {s.corpOverview}</div>
              <Editable as="h2" id="solar.profile.title" className="oj-h2 block">{s.title}</Editable>
              <div className="mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-amber-400 to-amber-500" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="space-y-5">
              <Editable as="p" id="solar.profile.p0" className="text-slate-600 leading-relaxed block">{s.p0}</Editable>
              <Editable as="p" id="solar.profile.p1" className="text-slate-600 leading-relaxed block">{s.p1}</Editable>
              <div className="oj-card p-6 border-l-4 !border-l-amber-400">
                <Editable as="p" id="solar.profile.p2" className="text-[#0B3B39] font-semibold leading-relaxed block">{s.p2}</Editable>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section sid="solar.scale" name="Engineering & Scale" className="oj-section">
        <div className="oj-container grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-teal-900/50">
              <img src={IMAGES.substation} alt="substation" className="w-full h-[380px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="oj-eyebrow mb-3">{s.engScale}</div>
            <h2 className="oj-h2 mb-6">{s.powering}</h2>
            <div className="space-y-5">
              {s.features.map(([title, desc]) => (
                <div key={title} className="flex gap-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-[#0B3B39]">{title}</h4>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>

      <Section sid="solar.specs" name="Specifications" className="bg-[#E9F5F4]/40 border-y border-teal-900/40 oj-section">
        <div className="oj-container">
          <SectionHeader eyebrow={s.engBaseline} title={s.specTitle} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {s.specs.map(([label, value], i) => {
              const Icon = SPEC_ICONS[i];
              return (
                <Reveal key={label} delay={i * 0.08}>
                  <div className="oj-card p-6 h-full">
                    <Icon className="w-7 h-7 text-teal-600 mb-4" />
                    <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
                    <div className="mt-1.5 font-semibold text-white">{value}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {s.stats.map(([v, l], i) => (
              <Reveal key={l} delay={i * 0.06}>
                <div className="oj-card p-6">
                  <div className="text-3xl font-extrabold gold-text font-mono">{v}</div>
                  <div className="mt-1 text-xs text-slate-600">{l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <ProjectShowcase
        category="solar_project"
        eyebrow={s.galleryEyebrow}
        title={s.galleryTitle}
        subtitle={s.gallerySub}
      />

      <Section sid="solar.cta" name="Feasibility CTA" className="oj-container py-20">
        <Reveal>
          <div className="oj-card p-10 flex flex-col md:flex-row items-center justify-between gap-6 border-amber-400/20">
            <div>
              <div className="oj-eyebrow mb-2"><Zap className="w-4 h-4" /> {s.feasEyebrow}</div>
              <h3 className="text-2xl font-bold text-white">{s.feasTitle}</h3>
              <p className="text-slate-600 mt-2">{s.feasDesc}</p>
            </div>
            <Link to="/solar-calculator" className="oj-btn-gold whitespace-nowrap" data-testid="solar-cta-calculator"><Calculator className="w-4 h-4" /> {s.openCalc}</Link>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
